import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseService } from './base.service';
import { IBaseModel } from '../models/base.model';
import {
    Encaminhamento,
    Papel,
    GerarUrl,
    AssinaturaDigitalValida,
    Documento,
    ProcessoAutuador,
    Processo,
    DocumentoValido,
    ProcessoLocalizacao,
    DocumentoSearchRequest,
    DocumentoSearchResponse,
    ProcessoPaginatedSearchRequest,
    ProcessoPaginatedSearchResponse,
    AgenteApi
} from '../models/edocs.model';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class EDocsService extends BaseService {
    controllerRoute = 'EDocs';

    constructor(httpClient: HttpClient) {
        super(httpClient);
    }

    public async getAgente(id: string): Promise<IBaseModel<AgenteApi>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Agente/${id}`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<AgenteApi>>(url));
    }

    public async getEncaminhamento(id: string): Promise<IBaseModel<Encaminhamento>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Encaminhamento/${id}`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<Encaminhamento>>(url));
    }

    public async getDocumento(id: string): Promise<IBaseModel<Documento>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documento/${id}`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<Documento>>(url));
    }

    public async getDocumentoDownloadUrl(id: string): Promise<IBaseModel<string>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documento/${id}/DownloadUrl`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<string>>(url));
    }

    public async getDocumentoPodeUsar(id: string): Promise<IBaseModel<boolean>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documento/${id}/PodeUsar`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<boolean>>(url));
    }

    public async getUsuarioPapeisEncaminhamento(id: string): Promise<IBaseModel<Papel[]>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Usuario/Papeis/Encaminhamento/${id}`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<Papel[]>>(url));
    }

    public async getGerarUrl(dataLength: number): Promise<IBaseModel<GerarUrl>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documento/GerarUrl/${dataLength}`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<GerarUrl>>(url));
    }

    public async getProcesso(id: string): Promise<IBaseModel<Processo>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Processo/${id}`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<Processo>>(url));
    }

    public async getProcessoAutuador(id: string): Promise<IBaseModel<ProcessoAutuador>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Processo/${id}/Autuador`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<ProcessoAutuador>>(url));
    }

    public async getProcessoLocalCustodia(id: string): Promise<IBaseModel<ProcessoLocalizacao>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Processo/${id}/LocalCustodia`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<ProcessoLocalizacao>>(url));
    }

    public async postDocumentoSearch(data: DocumentoSearchRequest): Promise<IBaseModel<DocumentoSearchResponse>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documentos/Search`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<DocumentoSearchResponse>>(url, data));
    }

    public async postProcessoPaginatedSearch(data: ProcessoPaginatedSearchRequest): Promise<IBaseModel<ProcessoPaginatedSearchResponse>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Processos/PaginatedSearch`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<ProcessoPaginatedSearchResponse>>(url, data));
    }

    public async postAssinaturaDigitalValida(identificadorTemporarioArquivoNaNuvem: string): Promise<IBaseModel<AssinaturaDigitalValida>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documento/AssinaturaDigitalValida`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<AssinaturaDigitalValida>>(url, { identificadorTemporarioArquivoNaNuvem }));
    }

    public async postDocumentoValidoNatoDigitalCopiaServidor(identificadorTemporarioArquivoNaNuvem: string): Promise<IBaseModel<DocumentoValido>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documento/DocumentoValidoNatoDigitalCopiaServidor`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<DocumentoValido>>(url, { identificadorTemporarioArquivoNaNuvem }));
    }

    public async postDocumentoValidoNatoDigitalCopiaCidadao(identificadorTemporarioArquivoNaNuvem: string): Promise<IBaseModel<DocumentoValido>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documento/DocumentoValidoNatoDigitalCopiaCidadao`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<DocumentoValido>>(url, { identificadorTemporarioArquivoNaNuvem }));
    }

    public async postDocumentoValidoNatoDigitalAutoAssinadoServidor(identificadorTemporarioArquivoNaNuvem: string): Promise<IBaseModel<DocumentoValido>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documento/DocumentoValidoNatoDigitalAutoAssinadoServidor`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<DocumentoValido>>(url, { identificadorTemporarioArquivoNaNuvem }));
    }

    public async postDocumentoValidoNatoDigitalAutoAssinadoCidadao(identificadorTemporarioArquivoNaNuvem: string): Promise<IBaseModel<DocumentoValido>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documento/DocumentoValidoNatoDigitalAutoAssinadoCidadao`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<DocumentoValido>>(url, { identificadorTemporarioArquivoNaNuvem }));
    }

    public async postDocumentoValidoDigitalizadoServidor(identificadorTemporarioArquivoNaNuvem: string): Promise<IBaseModel<DocumentoValido>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documento/DocumentoValidoDigitalizadoServidor`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<DocumentoValido>>(url, { identificadorTemporarioArquivoNaNuvem }));
    }

    public async postDocumentoValidoDigitalizadoCidadao(identificadorTemporarioArquivoNaNuvem: string): Promise<IBaseModel<DocumentoValido>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/Documento/DocumentoValidoDigitalizadoCidadao`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<DocumentoValido>>(url, { identificadorTemporarioArquivoNaNuvem }));
    }
}
